import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Copy, CheckCircle2, UserCheck } from 'lucide-react'
import { toast } from '@/components/ui/use-toast'
import { useState } from 'react'
import { Highlight, themes } from 'prism-react-renderer'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'

// Code examples constants
const IDENTIFY_CODE_EXAMPLE = `// Call this after a user logs into your site
window.feedbask.identify({
  userId: 'UNIQUE_USER_ID', // Required: A stable, unique ID from your system
  name: 'Ada Lovelace',      // Optional: Pre-fills name field
  email: 'ada@example.com',  // Optional: Pre-fills email field

  // You can add any other custom properties
  plan: 'premium',
  createdAt: '2023-10-27T10:00:00Z'
});`

const UNIDENTIFY_CODE_EXAMPLE = `// Call this when a user logs out of your site
window.feedbask.unidentify();`

interface WidgetInstallationProps {
	widgetId: string
}

const CodeBlock = ({ code, language }: { code: string; language: string }) => (
	<Highlight theme={themes.vsDark} code={code.trim()} language={language}>
		{({ className, style, tokens, getLineProps, getTokenProps }) => (
			<pre
				className="p-4 rounded-md font-mono text-sm overflow-x-auto border dark:border-gray-700"
				style={style}>
				{tokens.map((line, i) => (
					<div key={i} {...getLineProps({ line })}>
						{line.map((token, key) => (
							<span key={key} {...getTokenProps({ token })} />
						))}
					</div>
				))}
			</pre>
		)}
	</Highlight>
)

export const WidgetInstallation = ({ widgetId }: WidgetInstallationProps) => {
	const { t } = useTranslation()
	const [copied, setCopied] = useState(false)

	const getInstallCode = () => {
		// Basic script for now - could be customized per tech stack
		return `<script
    defer
    src="https://cdn.feedbask.com/widget.js"
    data-client-key="${widgetId}"
    data-language="en"
    data-size="m"
    id="feedbask-widget-script"
></script>`

	}


	const copyCode = () => {
		navigator.clipboard
			.writeText(getInstallCode())
			.then(() => {
				setCopied(true)
				toast({
					title: t('widgetInstallation.success'),
					description: t('widgetInstallation.installationCodeCopied'),
				})
				setTimeout(() => setCopied(false), 2000)
			})
			.catch(() => {
				toast({
					title: t('widgetInstallation.error'),
					description: t('widgetInstallation.failedToCopyCode'),
					variant: 'destructive',
				})
			})
	}

	return (
		<div>
			<Card className="mb-6">
				<CardHeader>
					<CardTitle>{t('widgetInstallation.widgetInstallation')}</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<div>
							<div className="text-sm text-muted-foreground mb-2">
								{t('widgetInstallation.projectId')}:
							</div>
							<code className="bg-secondary p-2 rounded-md text-sm">
								{widgetId}
							</code>
						</div>

						<div>
							<div className="text-sm text-muted-foreground mb-2">
								{t('widgetInstallation.installationCode')}:
							</div>
							<div className="mb-2">
								<CodeBlock code={getInstallCode()} language="html" />
							</div>
							<Button
								onClick={copyCode}
								variant={copied ? 'outline' : 'secondary'}
								className={cn(
									'w-full sm:w-auto transition-all duration-200',
									copied
										? 'bg-green-50 dark:bg-green-900/50 text-green-600 dark:text-green-300 border-green-200 dark:border-green-700'
										: ''
								)}>
								{copied ? (
									<>
										<CheckCircle2 className="mr-2 h-4 w-4" />
										{t('widgetInstallation.copied')}
									</>
								) : (
									<>
										<Copy className="mr-2 h-4 w-4" />
										{t('widgetInstallation.copyCode')}
									</>
								)}
							</Button>
						</div>
					</div>
				</CardContent>
			</Card>
			
			{/* NEW SECTION FOR IDENTIFY METHOD */}
			<Card className="mb-6">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<UserCheck className="h-5 w-5" />
						{t('widgetInstallation.identifyLoggedInUsers')}
					</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<p className="text-sm text-muted-foreground">
							{t('widgetInstallation.identifyDescription')}
						</p>
						
						<div>
							<h4 className="font-medium mb-2">{t('widgetInstallation.identifyUser')}</h4>
							<CodeBlock code={IDENTIFY_CODE_EXAMPLE} language="javascript" />
						</div>
						
						<div>
							<h4 className="font-medium mb-2">{t('widgetInstallation.unidentifyUser')}</h4>
							<CodeBlock code={UNIDENTIFY_CODE_EXAMPLE} language="javascript" />
						</div>
						
						<Alert>
							<AlertTitle>{t('widgetInstallation.important')}</AlertTitle>
							<AlertDescription>
								{t('widgetInstallation.userIdImportant')}
							</AlertDescription>
						</Alert>
					</div>
				</CardContent>
			</Card>
			
			<Card className="mb-6">
				<CardContent>
					<div className="space-y-4">
						{/* Language Configuration Tip */}
					<div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800">
						<div className="flex items-start gap-3">
							<div className="p-1 bg-blue-100 dark:bg-blue-800 rounded-full flex-shrink-0">
								<svg className="h-4 w-4 text-blue-600 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
								</svg>
							</div>
							<div>
								<p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
									{t('widgetInstallation.languageConfiguration')}
								</p>
								<p className="text-sm text-blue-700 dark:text-blue-300">
									{t('widgetInstallation.languageConfigurationDescription')}
								</p>
								<ul className="text-sm text-blue-700 dark:text-blue-300 mt-2 ml-2">
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-language="en"</code> {t('widgetInstallation.forEnglish')}</li>
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-language="fr"</code> {t('widgetInstallation.forFrench')}</li>
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-language="de"</code> {t('widgetInstallation.forGerman')}</li>
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-language="es"</code> {t('widgetInstallation.forSpanish')}</li>
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-language="zh"</code> {t('widgetInstallation.forChinese')}</li>
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-language="km"</code> {t('widgetInstallation.forKhmer')}</li>
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-language="vi"</code> {t('widgetInstallation.forVietnamese')}</li>
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-size="s|m|l"</code> Trigger size (small, medium, large)</li>
									<li>• <code className="bg-blue-100 dark:bg-blue-800 px-1 py-0.5 rounded text-xs font-mono">data-icon="https://your-cdn/icon.svg"</code> Replace the default icon with your image</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
		</div>
	)
}
